import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import HomePage from './Pages/Home';
import VehiclesPage from './Pages/VehiclesPage';
import AboutPage from './Pages/AboutPage';
import ContactPage from './Pages/ContactPage';
import BookingForm from './common/BookingForm';
import FloatingContactButtons from './common/FloatingContactButtons ';
import ScrollToTop from './common/ScrollToTop';
import BlogPage from '../src/Pages/BlogPage';
import PrivacyPolicy from './Pages/PrivacyPolicy'
import TermsOfService from './Pages/TermsOfService';
import CookiesPolicy from './Pages/CookiesPolicy';
import { Cookie, X, Check, Settings, Shield } from 'lucide-react';
import TourPackages from './Pages/TourPage'
import LuxaryvehiclesPage from './Pages/LuxaryvehiclesPage'

const App = () => {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  useEffect(() => {
    // Check if user has already made cookie choices
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowCookieBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const preferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowCookieBanner(false);
    
    // Implement your actual cookie setting logic here
    initializeCookies(preferences);
  };

  const handleRejectAll = () => {
    const preferences = {
      necessary: true, // Necessary cookies can't be rejected
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowCookieBanner(false);
    
    // Implement your actual cookie setting logic here
    initializeCookies(preferences);
  };

  const handleCustomize = () => {
    setShowCookieModal(true);
  };

  const handleSavePreferences = (preferences) => {
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString()
    }));
    setShowCookieBanner(false);
    setShowCookieModal(false);
    
    // Implement your actual cookie setting logic here
    initializeCookies(preferences);
  };

  const initializeCookies = (preferences) => {
    // This is where you implement your actual cookie logic
    console.log('Initializing cookies with preferences:', preferences);
    
    // Example: Initialize Google Analytics only if analytics cookies are accepted
    if (preferences.analytics) {
      // Initialize Google Analytics
      // gtag('config', 'GA_MEASUREMENT_ID');
    }
    
    if (preferences.marketing) {
      // Initialize Facebook Pixel, etc.
      // fbq('init', 'PIXEL_ID');
    }
    
    // Necessary cookies are always initialized
    // Initialize session management, etc.
  };

  // Cookie preferences for the modal
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Necessary Cookies',
      description: 'Essential for the website to function properly',
      required: true,
      duration: 'Session to 1 year'
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Remember your preferences and settings',
      required: false,
      duration: '1 year'
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website',
      required: false,
      duration: '2 years'
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to track visitors across websites for advertising',
      required: false,
      duration: '1 year'
    }
  ];

  const handlePreferenceChange = (cookieType) => {
    if (cookieType === 'necessary') return; // Necessary cookies can't be disabled
    
    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <ScrollToTop />
        
        {/* Cookie Consent Banner */}
        {showCookieBanner && (
          <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
            <div className="max-w-7xl mx-auto p-4">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-w-4xl mx-auto">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">We Value Your Privacy</h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      We use cookies to enhance your browsing experience, serve personalized ads or content, 
                      and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                      Customize your preferences in the cookie settings.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                      >
                        <Check className="w-4 h-4" />
                        <span>Accept All</span>
                      </button>
                      <button
                        onClick={handleCustomize}
                        className="bg-white text-gray-700 px-6 py-3 rounded-xl font-semibold border border-gray-300 hover:border-blue-500 hover:text-blue-600 transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Customize</span>
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        Reject All
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      By continuing, you agree to our{' '}
                      <a href="/privacy" className="text-blue-600 hover:underline font-medium">Privacy Policy</a> 
                      {' '}and{' '}
                      <a href="/terms" className="text-blue-600 hover:underline font-medium">Terms of Service</a>.
                    </p>
                  </div>
                  <button
                    onClick={handleRejectAll}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0 mt-1"
                    aria-label="Close cookie banner"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cookie Preferences Modal */}
        {showCookieModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Cookie Preferences</h2>
                      <p className="text-blue-100 text-sm">Choose which cookies you accept</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCookieModal(false)}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors backdrop-blur-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4 mb-6">
                  {cookieTypes.map((type) => (
                    <div key={type.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-2xl flex items-center justify-center ${
                            type.id === 'necessary' ? 'bg-green-100' :
                            type.id === 'functional' ? 'bg-blue-100' :
                            type.id === 'analytics' ? 'bg-purple-100' : 'bg-amber-100'
                          }`}>
                            {type.id === 'necessary' && <Shield className="w-4 h-4 text-green-600" />}
                            {type.id === 'functional' && <Settings className="w-4 h-4 text-blue-600" />}
                            {type.id === 'analytics' && <Cookie className="w-4 h-4 text-purple-600" />}
                            {type.id === 'marketing' && <Cookie className="w-4 h-4 text-amber-600" />}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{type.name}</h3>
                            <p className="text-sm text-gray-600">{type.description}</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={cookiePreferences[type.id]}
                            onChange={() => handlePreferenceChange(type.id)}
                            disabled={type.required}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 rounded-full peer ${
                            type.required ? 'bg-green-300' : 'bg-gray-200 peer-checked:bg-blue-600'
                          } peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors`}>
                            <div className={`absolute top-0.5 left-0.5 bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform ${
                              cookiePreferences[type.id] ? 'transform translate-x-5' : ''
                            } ${type.required ? 'bg-green-100' : ''}`} />
                          </div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center space-x-1">
                            <span>Duration: {type.duration}</span>
                          </span>
                          {type.required && (
                            <span className="flex items-center space-x-1 text-green-600 font-medium">
                              <Shield className="w-3 h-3" />
                              <span>Always active</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-600 text-sm">ℹ️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">About Your Choices</h4>
                      <p className="text-blue-800 text-sm">
                        Necessary cookies are required for the website to function and cannot be switched off. 
                        You can update these preferences anytime in our Cookies Policy page.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleSavePreferences(cookiePreferences)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                  >
                    <Check className="w-4 h-4" />
                    <span>Save Preferences</span>
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Reject All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/vehicles" element={<VehiclesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/tourpackages" element={<TourPackages />} />
          <Route path="/luxaryvehicles" element={<LuxaryvehiclesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/booking-form" element={<BookingForm />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
        </Routes>
        <Footer />
        <FloatingContactButtons />
      </div>
    </Router>
  );
};

export default App;